import React, { useEffect } from 'react';
import { useAuth } from 'auth/AuthProvider';
import { RouteEnum } from 'modules/account/constantes/RouteEnum';
import { ApiLocalStorage } from 'utils/ApiLocalStorage';
import {
  isPathPublicFederatedPage,
  type FederatedPageModuleLike,
} from 'utils/federatedRouteMatch';
import { USER, USER_DATA_KEY } from '../../constantes/LocalStorageEnum';

export interface SessionWatcherOptions {
  modules?: FederatedPageModuleLike[];
}

// todo: chamar hook de limpar dados
// todo: padronizar hooks

export const resetStorage = () => {
  ApiLocalStorage.remove(USER_DATA_KEY);
  ApiLocalStorage.remove(USER);
  ApiLocalStorage.remove('@pwa-username');
  ApiLocalStorage.remove('@pwa-participantId');
  ApiLocalStorage.remove('@pwa-participant-id');
  ApiLocalStorage.remove('@pwa:access-token');
  ApiLocalStorage.remove('@pwa:otp-data');
  ApiLocalStorage.remove('@pwa:search-user-data');
  ApiLocalStorage.remove('currentBrand');
  ApiLocalStorage.remove('currentFaixa');
  ApiLocalStorage.remove('currentGeneric');
  ApiLocalStorage.remove('currentTier');
  ApiLocalStorage.remove('participant_segments_all');
  ApiLocalStorage.remove('session-expiration');

  ApiLocalStorage.remove('segmentations_cache');
  ApiLocalStorage.remove('segmentations_timestamp');
  ApiLocalStorage.remove('performance_cache');
  ApiLocalStorage.remove('performance_timestamp');

}

/** Verifica se a URL é externa (http/https) e exige sair do app via window.location. */
const isExternalUrl = (url: string) => /^https?:\/\//i.test(url);

/**
 * Resolve o destino de redirecionamento pós-logout.
 * Lê `login.logout_url` do tema (theme/default.json); cai para a tela de login padrão.
 */
export const getLogoutRedirectUrl = (): string => {
  const theme = ApiLocalStorage.get('theme') || {};

  return theme?.login?.logout_url || RouteEnum.LOGIN_USERNAME;
};

const checkIfLoggedIn = async (
  logout: () => Promise<void>,
  navigate: (path: string) => void,
  location: string,
  modules?: FederatedPageModuleLike[],
) => {
  const currentPath = location;

  /** Rotas públicas: não exige `session-expiration` (evita mandar para login em URLs com `:id`). */
  if (
    currentPath === RouteEnum.WELCOME ||
    currentPath.startsWith(RouteEnum.SIMULATOR) ||
    currentPath.startsWith(RouteEnum.HOW_TO_JOIN_POINTS)
  ) {
    return;
  }

  if (modules?.length && isPathPublicFederatedPage(modules, currentPath)) {
    return;
  }

  const expirationTime = ApiLocalStorage.get('session-expiration');

  if (!expirationTime || new Date().getTime() > Number(expirationTime)) {
    const urlsLogin = Object.values(RouteEnum).filter((path) => path !== RouteEnum.HOME) as string[];

    if (currentPath && !urlsLogin.includes(currentPath)) {
      const redirectUrl = getLogoutRedirectUrl();

      resetStorage();
      await logout();

      if (isExternalUrl(redirectUrl)) {
        window.location.replace(redirectUrl);
      } else {
        navigate(redirectUrl);
      }
    }
  }
};

export const startSessionWatcher = (
  logout: () => Promise<void>,
  navigate: (path: string) => void,
  location: string,
  options?: SessionWatcherOptions,
) => {
  const modules = options?.modules;

  checkIfLoggedIn(logout, navigate, location, modules);
  
  const handleStorageChange = (event: StorageEvent) => {
    if (event.key === 'session-expiration' && event.newValue === null) {
      checkIfLoggedIn(logout, navigate, location, modules);
    }
  };
  
  const interval = setInterval(() => {
    checkIfLoggedIn(logout, navigate, location, modules);
  }, 30000);
  
  window.addEventListener('storage', handleStorageChange);
  
  return () => {
    clearInterval(interval);
    window.removeEventListener('storage', handleStorageChange);
  };
};

const LogoutPage = () => {
  const {logout} = useAuth();

  useEffect(() => {
    const handleLogout = async () => {
      const redirectUrl = getLogoutRedirectUrl();

      resetStorage();
      await logout();
      setTimeout(() => {
        window.location.replace(redirectUrl);
      }, 200);
    }
    
    handleLogout();
  }, []);
  
  return (
    <></>
  )
}

export default LogoutPage;
